// SEQ-180 phase 1 data hooks. `pnl_analytics_query`
// (supabase/migrations/20260908000500_pnl_analytics_query.sql) is being
// built in parallel and is not callable against any database yet, so these
// hooks fall back to a fixture in development only, and only when the RPC
// itself is missing (never on an authorisation error or any other failure,
// which are real and must surface to the user).
//
// CONTRACT CHANGE (round 3 addendum): the RPC's groupBy no longer offers a
// combined project+month grain, only project | month | quarter |
// calendar_year | financial_year | builder | status. The page needs both a
// project-grain call (bar chart, both scatters, the table, and the KPI
// company total built by summing these) and a month-grain call (the
// company-wide trend line), so `usePnlAnalytics` makes two calls, not one,
// built on the same underlying `useOnePnlAnalyticsGrain`. Switching the
// trend widget to a single project needs its own on-demand
// `usePnlAnalyticsProjectTrend` call (groupBy: "month", projectIds: [id]),
// since no row on this page carries both a projectId and a periodMonth any
// more (see rollup.ts).

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { callPnlAnalytics } from "../../lib/pnl/analyticsClient";
import type { PnlAnalyticsFilters, PnlAnalyticsResponse } from "../../lib/pnl/analyticsClient";
import { FIXTURE_PNL_ANALYTICS_PROJECT_RESPONSE, FIXTURE_PNL_ANALYTICS_MONTH_RESPONSE, buildFixtureProjectTrendResponse } from "./fixtures";
import { PNL_ROLLUP_METRICS } from "./rollup";
import { grainRequestKey } from "./projectMonthView";

export interface PnlAnalyticsQueryFilters {
  from: string | null;
  to: string | null;
  projectIds: string[];
  statuses: string[];
}

/**
 * SEQ-182: exported so `useProjectOperationalHours.ts` (the P&L tab's
 * operational hours band) can reuse the exact same debounced-call,
 * dev-fixture-fallback, error-surfacing machinery for a metrics list of its
 * own, rather than duplicating it. See `useOnePnlAnalyticsGrain` below.
 */
export interface UsePnlAnalyticsGrainResult {
  data: PnlAnalyticsResponse | null;
  /** SEQ-193: the request key `data` belongs to, so a caller can refuse rows fetched for different filters while a refetch is pending. Null when there is no data. */
  dataKey: string | null;
  loading: boolean;
  error: string | null;
  isFixtureData: boolean;
  refresh: () => void;
}

const DEBOUNCE_MS = 350;

/**
 * Matches PostgREST's "no such function" responses (missing RPC, code
 * PGRST202) and a raw Postgres 42883 undefined_function, but nothing else,
 * so a real authorisation or data error still reaches the user as an error
 * rather than silently swapping in fixture data.
 */
const RPC_MISSING_PATTERN = /does not exist|could not find the function|PGRST202/i;

/**
 * One grain, one debounced call, DEV-only fixture fallback. `enabled: false`
 * skips fetching entirely (used when no project is selected for the trend
 * drill).
 *
 * SEQ-182: `metrics` is now a parameter rather than the hardcoded
 * `PNL_ROLLUP_METRICS` constant, and the function is exported. This is a
 * pure widening: every existing caller below still passes
 * `PNL_ROLLUP_METRICS` explicitly, so behaviour is unchanged for them.
 * `useProjectOperationalHours.ts` is the new caller, requesting only the
 * three hours keys the P&L tab band needs rather than the page's full
 * seven-metric rollup set.
 */
export function useOnePnlAnalyticsGrain(
  filters: PnlAnalyticsQueryFilters,
  groupBy: "project" | "month" | "project_month",
  metrics: string[],
  fixtureResponse: PnlAnalyticsResponse,
  enabled: boolean,
): UsePnlAnalyticsGrainResult {
  const [data, setData] = useState<PnlAnalyticsResponse | null>(null);
  const [dataKey, setDataKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [isFixtureData, setIsFixtureData] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  // Arrays are new references every render, so comparing the serialised
  // filters/metrics (not the objects) is what makes the effect below fire
  // once per actual change rather than once per render.
  const filterKey = JSON.stringify(filters);
  const metricsKey = JSON.stringify(metrics);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setDataKey(null);
      setLoading(false);
      setError(null);
      setIsFixtureData(false);
      return;
    }

    let cancelled = false;
    const requestKey = grainRequestKey(filters, groupBy, metrics);

    const handle = setTimeout(() => {
      const requestFilters: PnlAnalyticsFilters = {
        from: filters.from,
        to: filters.to,
        projectIds: filters.projectIds,
        statuses: filters.statuses,
        groupBy,
        metrics,
        schemaVersion: 1,
      };

      setLoading(true);
      setError(null);

      callPnlAnalytics(supabase, requestFilters)
        .then((response: PnlAnalyticsResponse) => {
          if (cancelled) return;
          setData(response);
          setDataKey(requestKey);
          setIsFixtureData(false);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          const message = err instanceof Error ? err.message : String(err);

          if (import.meta.env.DEV && RPC_MISSING_PATTERN.test(message)) {
            setData(fixtureResponse);
            setDataKey(requestKey);
            setIsFixtureData(true);
            setError(null);
            return;
          }

          setData(null);
          setDataKey(null);
          setIsFixtureData(false);
          setError(message);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- setTimeout debounce window, plan section 9 E14
    }, DEBOUNCE_MS) as any;

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterKey/metricsKey are the real dependencies; filters/metrics/fixtureResponse are fresh references every render
  }, [filterKey, metricsKey, refreshToken, enabled, groupBy]);

  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);

  return { data, dataKey, loading, error, isFixtureData, refresh };
}

export interface UsePnlAnalyticsResult {
  projectRows: PnlAnalyticsResponse["rows"] | null;
  monthRows: PnlAnalyticsResponse["rows"] | null;
  meta: PnlAnalyticsResponse["meta"] | null;
  loading: boolean;
  error: string | null;
  isFixtureData: boolean;
  refresh: () => void;
}

/** The page's two standing calls: project-grain (bar/scatters/table/KPI) and month-grain (company trend). */
export function usePnlAnalytics(filters: PnlAnalyticsQueryFilters): UsePnlAnalyticsResult {
  const projectResult = useOnePnlAnalyticsGrain(filters, "project", PNL_ROLLUP_METRICS, FIXTURE_PNL_ANALYTICS_PROJECT_RESPONSE, true);
  const monthResult = useOnePnlAnalyticsGrain(filters, "month", PNL_ROLLUP_METRICS, FIXTURE_PNL_ANALYTICS_MONTH_RESPONSE, true);

  // Plain function, not useCallback: it only calls two already-stable
  // refresh functions, so memoizing this wrapper buys nothing and the React
  // Compiler cannot infer a dependency array narrower than the whole
  // projectResult/monthResult objects here anyway.
  const refresh = () => {
    projectResult.refresh();
    monthResult.refresh();
  };

  return {
    projectRows: projectResult.data?.rows ?? null,
    monthRows: monthResult.data?.rows ?? null,
    meta: projectResult.data?.meta ?? monthResult.data?.meta ?? null,
    loading: projectResult.loading || monthResult.loading,
    error: projectResult.error ?? monthResult.error,
    isFixtureData: projectResult.isFixtureData || monthResult.isFixtureData,
    refresh,
  };
}

/**
 * On-demand month-grain trend for one project, used by the trend widget's
 * "view: <project>" selector. Returns idle (no fetch) while `projectId` is
 * null, which is what "Company total" in that selector means.
 */
export function usePnlAnalyticsProjectTrend(
  filters: PnlAnalyticsQueryFilters,
  projectId: string | null,
): UsePnlAnalyticsGrainResult {
  const scopedFilters = useMemo<PnlAnalyticsQueryFilters>(
    () => ({ ...filters, projectIds: projectId ? [projectId] : filters.projectIds }),
    [filters, projectId],
  );
  const fixtureResponse = useMemo(
    () => (projectId ? buildFixtureProjectTrendResponse(projectId) : { rows: [], meta: FIXTURE_PNL_ANALYTICS_MONTH_RESPONSE.meta }),
    [projectId],
  );

  return useOnePnlAnalyticsGrain(scopedFilters, "month", PNL_ROLLUP_METRICS, fixtureResponse, projectId !== null);
}
